import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { CatalogEntry, Mode } from '../games/catalog';
import previewIds from '../games/previews.json';
import { sound } from '../lib/audio';
import { isNew, releaseYear } from '../games/fresh';
import { shortCount, useGameStats } from '../lib/gameStats';
import { NavIcon } from './NavIcons';

/** Games with a short silent gameplay clip (scripts/make-previews.mjs) that plays while the tile is hovered. */
const PREVIEWS = new Set<string>(previewIds);
/** Only on devices with a real mouse: phones have no hover, and the clips would just use up data. */
const canHover = typeof matchMedia === 'function' && matchMedia('(hover: hover) and (pointer: fine)').matches;
/** The homepage's big top tile plays its clip by itself, unless the visitor asked for less motion or less data. */
const canAutoPreview =
  canHover &&
  !matchMedia('(prefers-reduced-motion: reduce)').matches &&
  !(navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;

const MODE_LABEL: Record<Mode, string> = { solo: 'Solo', keyboard: 'Keyboard', touch: 'Touch' };

/** Cover tile. On hover the gameplay clip plays and the name, release year, likes and plays slide up. */
export function GameTile({ game, index, to, featured = false, autoPreview = false }: { game: CatalogEntry; index: number; to?: string; featured?: boolean; autoPreview?: boolean }) {
  const modes = game.modes.map((m) => MODE_LABEL[m]).join(' · ');
  const { plays, likes } = useGameStats(game.id);
  const [preview, setPreview] = useState(false);
  const [playing, setPlaying] = useState(false);
  const timer = useRef(0);
  const hasPreview = canHover && PREVIEWS.has(game.id);
  const auto = autoPreview && canAutoPreview && PREVIEWS.has(game.id);
  // Wait until the cover has shown for a moment, so the clip never slows the first paint.
  useEffect(() => {
    if (!auto) return;
    const t = window.setTimeout(() => setPreview(true), 1200);
    return () => clearTimeout(t);
  }, [auto, game.id]);
  // A short wait first, so sweeping the mouse across a row doesn't start a dozen downloads.
  const enter = () => {
    if (hasPreview) timer.current = window.setTimeout(() => setPreview(true), 180);
  };
  const leave = () => {
    clearTimeout(timer.current);
    setPreview(false);
    setPlaying(false);
  };
  return (
    <Link
      to={to ?? game.path}
      className={`tile${featured ? ' tile--featured' : ''}`}
      onClick={() => sound.unlock()}
      onMouseEnter={enter}
      onMouseLeave={leave} aria-label={`${game.name}${isNew(game) ? ' (new)' : ''}: ${modes}`}>
      <span className={`tile__thumb tile__thumb--${index % 6}${game.thumb ? ' tile__thumb--img' : ''}`} aria-hidden="true">
        {game.thumb ? (
          // Small tiles use the 400×300 copy (scripts/make-small-covers.sh); big featured tiles keep the full 640×480 cover.
          <img src={featured ? game.thumb : game.thumb.replace('/covers/', '/covers-sm/')} alt="" loading={featured ? 'eager' : 'lazy'} decoding="async" width={featured ? 640 : 400} height={featured ? 480 : 300} />
        ) : (
          <game.Icon />
        )}
        {preview && (
          <video className={`tile__video${playing ? ' is-playing' : ''}`} src={`/previews/${game.id}.webm`} muted loop playsInline autoPlay preload="auto" onPlaying={() => setPlaying(true)} />
        )}
      </span>
      {isNew(game) && (
        <span className="tile__badge" aria-hidden="true">
          NEW
        </span>
      )}
      <span className="tile__label" aria-hidden="true">
        <span className="tile__name">{game.name}</span>
        <span className="tile__meta">
          <span>
            <NavIcon name="calendar" size={13} />
            {releaseYear(game)}
          </span>
          <span>
            <NavIcon name="like" size={13} />
            {shortCount(likes)}
          </span>
          <span>
            <NavIcon name="plays" size={13} />
            {shortCount(plays)}
          </span>
        </span>
      </span>
    </Link>
  );
}
